import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import WebApp from '@twa-dev/sdk';
import { PostHogProvider } from 'posthog-js/react';
import { changeLanguage, SUPPORTED_LANGS } from './i18n';

// Define types based on actual WebApp usage
type WebAppInitData = typeof WebApp.initDataUnsafe;
type ThemeParams = typeof WebApp.themeParams;
type PopupParams = Parameters<typeof WebApp.showPopup>[0];
type EventType = Parameters<typeof WebApp.onEvent>[0];
type EventHandler = Parameters<typeof WebApp.onEvent>[1];

interface WebAppContextType {
  isReady: boolean;
  initData: string;
  initDataUnsafe: WebAppInitData;
  colorScheme: "light" | "dark";
  themeParams: ThemeParams;
  showPopup: (params: PopupParams) => void;
  expand: () => void;
  onEvent: (eventType: EventType, eventHandler: EventHandler) => void;
  offEvent: (eventType: EventType, eventHandler: EventHandler) => void;
}

const WebAppContext = createContext<WebAppContextType | null>(null);

export const WebAppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    
    // Initialize WebApp only once
    try {
      WebApp.ready();
      WebApp.expand();
      
      // Check if WebApp has initData
      if (WebApp.initData && WebApp.initDataUnsafe?.user) {
        console.log('WebApp initialized successfully with user data');
        
        // Set language based on Telegram user's language_code if available
        const userLang = WebApp.initDataUnsafe.user.language_code;
        if (userLang) {
          const baseLang = userLang.split('-')[0] as typeof SUPPORTED_LANGS[number];
          if (SUPPORTED_LANGS.includes(baseLang)) {
            console.log(`Setting language from Telegram user: ${baseLang}`);
            changeLanguage(baseLang);
          }
        }
      } else {
        console.warn('WebApp initialized but no user data found. Make sure you open this app through Telegram.');
      }
      
      // Mark as ready even if data is missing, to allow authentication flow to decide what to do
      setIsReady(true);
    } catch (error) {
      console.error('Error initializing WebApp:', error);
      // Still mark as ready so we can show an appropriate error message
      setIsReady(true);
    }
  }, []);

  // Memoized functions to prevent unnecessary rerenders
  const showPopup = useCallback((params: PopupParams) => WebApp.showPopup(params), []);
  const expand = useCallback(() => WebApp.expand(), []);
  const onEvent = useCallback((eventType: EventType, eventHandler: EventHandler) => 
    WebApp.onEvent(eventType, eventHandler), []);
  const offEvent = useCallback((eventType: EventType, eventHandler: EventHandler) => 
    WebApp.offEvent(eventType, eventHandler), []);

  // Stable object that wraps WebApp functionality
  const webAppValue = useMemo(() => ({
    isReady,
    initData: WebApp.initData,
    initDataUnsafe: WebApp.initDataUnsafe,
    colorScheme: WebApp.colorScheme,
    themeParams: WebApp.themeParams,
    showPopup,
    expand,
    onEvent,
    offEvent
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    isReady,
    WebApp.initData,
    WebApp.initDataUnsafe,
    WebApp.colorScheme,
    WebApp.themeParams,
    showPopup,
    expand,
    onEvent,
    offEvent
  ]);

  return (
    <WebAppContext.Provider value={webAppValue}>
      <PostHogProvider
        apiKey={import.meta.env.VITE_POSTHOG_KEY}
        options={{
          api_host: import.meta.env.VITE_POSTHOG_HOST,
          debug: import.meta.env.MODE === "development",
        }}
      >
        {children}
      </PostHogProvider>
    </WebAppContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useWebApp = () => {
  const context = useContext(WebAppContext);
  if (!context) {
    throw new Error('useWebApp must be used within a WebAppProvider');
  }
  return context;
};